import type { ReactElement, ReactNode } from "react"
import styled from "styled-components"
import type { AudienceTone } from "~/shared/theme/tokens"

const Root = styled.article`
  position: relative;
  min-width: 0;
`

/**
 * Padding-top reserves the header's row (`headerMinHeight` plus the gap on
 * either side of it) so the media never renders underneath the label or the
 * icons that `Header` lays over this box from outside.
 *
 * Plain draws neither, so it reserves nothing and paints nothing: the artwork
 * goes straight onto the page.
 */
const Block = styled.div<{ $tone: AudienceTone; $plain: boolean }>`
  padding: ${({ theme, $plain }) =>
    $plain
      ? "0"
      : `calc(${theme.spacing.xs} + ${theme.card.headerMinHeight} + ${theme.spacing.xs}) ${theme.spacing.sm} ${theme.spacing.sm}`};
  border-radius: ${({ theme }) => theme.radius.md};
  background-color: ${({ $tone, $plain }) => ($plain ? "transparent" : $tone.block)};
`

/**
 * Positioned over `Block`'s top strip rather than nested inside it, so its icon
 * anchors are never descendants of the body link that wraps `Block` — an anchor
 * inside an anchor is invalid HTML that browsers repair unpredictably. Sitting
 * above the link in paint order, it also absorbs clicks on its own empty space
 * instead of letting them fall through to the link.
 */
const Header = styled.div`
  position: absolute;
  top: ${({ theme }) => theme.spacing.xs};
  left: ${({ theme }) => theme.spacing.sm};
  right: ${({ theme }) => theme.spacing.sm};
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing.xs};
  min-height: ${({ theme }) => theme.card.headerMinHeight};
`

/**
 * 11px at semibold rather than 10px at bold. Tracked uppercase this small is
 * nearly all stroke, and the page antialiases light text on a saturated ground,
 * which at the heavier weight closes the counters up into a blur.
 */
const AudienceLabel = styled.span<{ $tone: AudienceTone }>`
  font-size: 0.6875rem;
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  letter-spacing: 0.09em;
  text-transform: uppercase;
  line-height: ${({ theme }) => theme.lineHeight.tight};
  color: ${({ $tone }) => $tone.label};
`

/** `gap` keeps the book and Manage Access icons from touching when both are present. */
const Action = styled.div<{ $tone: AudienceTone }>`
  display: inline-flex;
  margin-left: auto;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xxs};
  color: ${({ $tone }) => $tone.label};
`

/**
 * The well is painted only when there is nothing to put in it. Cover art
 * arrives at ratios its family's well does not always match, and a `contain`
 * fit mats the difference — onto a second colour this would read as a
 * placeholder showing through the artwork, so the mat is the card's own block
 * and the poster colour appears only behind a fallback title.
 */
const Media = styled.div<{ $tone: AudienceTone; $hasCoverArt: boolean; $plain: boolean }>`
  position: relative;
  border-radius: ${({ theme }) => theme.radius.md};
  background-color: ${({ theme, $tone, $hasCoverArt, $plain }) => {
    if ($hasCoverArt) {
      return "transparent"
    }

    /* A plain card still needs a ground under the fallback title — a neutral
       one, since the point of plain is that no brand colour is showing. */
    return $plain ? theme.color.surfaceSubtle : $tone.poster
  }};
`

const BadgeSlot = styled.div`
  position: absolute;
  top: ${({ theme }) => theme.spacing.xs};
  left: ${({ theme }) => theme.spacing.xs};
  display: flex;
  gap: ${({ theme }) => theme.spacing.xxs};
  max-width: calc(100% - ${({ theme }) => theme.spacing.md});
`

/** Top-right, so a date can never collide with the status pill on the left. */
const ChipSlot = styled.div`
  position: absolute;
  top: ${({ theme }) => theme.spacing.xs};
  right: ${({ theme }) => theme.spacing.xs};
  display: flex;
  gap: ${({ theme }) => theme.spacing.xxs};
  max-width: calc(100% - ${({ theme }) => theme.spacing.md});
`

const FallbackTitleWrap = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => theme.spacing.sm};
`

/**
 * Stands in for the cover when there is none, so the card never renders as an
 * anonymous colour box. Clamped at the same reserved height as a real cover's
 * title would take, and coloured for the well it sits on rather than for the
 * page, since there is no page-background title to match here — which on a
 * plain card is the page's own text colour, because that well is neutral.
 */
const FallbackTitle = styled.h3<{ $tone: AudienceTone; $plain: boolean }>`
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  line-height: ${({ theme }) => theme.lineHeight.tight};
  color: ${({ theme, $tone, $plain }) => ($plain ? theme.color.textPrimary : $tone.label)};
  height: ${({ theme }) => theme.card.titleHeight};
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow-wrap: anywhere;
  text-align: center;
`

/** No wrapper at all — the default for a card with nothing to link to. */
const identityLink = (body: ReactNode): ReactElement => <>{body}</>

interface CardProps {
  /**
   * Feeds the accessible name callers put on `bodyLink`'s anchor and, when
   * there is no cover art, the visible fallback drawn in its place — the cover
   * itself carries the title otherwise, so nothing here renders it a second time.
   */
  readonly title: string
  readonly media: ReactNode
  /** Whether `media` is real cover art. False draws `title` in the well instead of an empty colour box. */
  readonly hasCoverArt?: boolean
  /** The card's colours. A real ministry brings its own; everything else wears the section's. */
  readonly tone: AudienceTone
  /**
   * Drops the card's chrome: no tinted block behind the artwork, and no header,
   * so `label` and `action` are not drawn at all. The covers then sit on the
   * page background with nothing around them.
   *
   * The badge and the chip stay either way — they are status the customer needs
   * (`Expired 5/1/26`), not decoration, and they sit over the artwork rather
   * than on the chrome this removes.
   */
  readonly plain?: boolean
  /** The audience name, where there is one to show. Omitted for a card the classifier could not place. */
  readonly label?: string
  /** Overlaid top-left on the artwork. Status, not decoration — a word or a date. */
  readonly badge?: ReactNode
  /** Overlaid top-right. */
  readonly chip?: ReactNode
  /**
   * The right end of the header row. MUST NOT contain the card's own link — the
   * header sits beside `bodyLink`'s anchor, never inside it. See `LibraryCard`.
   */
  readonly action?: ReactNode
  /**
   * Wraps the poster — a router link, an external anchor, or left as-is for a
   * lapsed card with nothing to open. Never wraps the header: its icon anchors
   * would nest inside another anchor otherwise, which is invalid HTML that
   * browsers repair unpredictably. Since the poster is the card's only visible
   * text, callers MUST give this anchor an accessible name from `title`.
   */
  readonly bodyLink?: (body: ReactNode) => ReactElement
  readonly qaHook: string
}

export const Card = ({
  title,
  media,
  hasCoverArt = true,
  tone,
  plain = false,
  label,
  badge,
  chip,
  action,
  bodyLink = identityLink,
  qaHook,
}: CardProps): ReactElement => (
  <Root data-qa-hook={qaHook} data-card-chrome={plain ? "plain" : "tinted"}>
    {bodyLink(
      <Block $tone={tone} $plain={plain}>
        <Media $tone={tone} $hasCoverArt={hasCoverArt} $plain={plain}>
          {media}
          {!hasCoverArt && (
            <FallbackTitleWrap>
              <FallbackTitle $tone={tone} $plain={plain}>
                {title}
              </FallbackTitle>
            </FallbackTitleWrap>
          )}
          {badge ? <BadgeSlot data-qa-hook={`${qaHook}-badge`}>{badge}</BadgeSlot> : null}
          {chip ? <ChipSlot data-qa-hook={`${qaHook}-chip`}>{chip}</ChipSlot> : null}
        </Media>
      </Block>,
    )}
    {plain ? null : (
      <Header data-qa-hook={`${qaHook}-header`}>
        {label ? (
          <AudienceLabel $tone={tone} data-qa-hook={`${qaHook}-audience`}>
            {label}
          </AudienceLabel>
        ) : null}
        {action ? <Action $tone={tone}>{action}</Action> : null}
      </Header>
    )}
  </Root>
)
